import {Component, ViewChild, HostListener} from '@angular/core';
import {AppService} from "../app.service";
import {MessageService, SelectItem} from "primeng/api";
import {JsonCase, JsonCaseTongue, JsonTongue, SimpleTongueDto, SpecificTongueCaseDto, TaggerType, TongueType} from "../../model/dto";
import {TongueSelectDialogComponent} from "./tongue-select-dialog/tongue-select-dialog.component";
import {SeverityEnum, ToastMsg} from "../../model/page";
import {TongueLocationPipe} from "../utility/tongue-location.pipe";
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-tongue-to-case',
  templateUrl: './tongue-to-case.component.html',
  styleUrl: './tongue-to-case.component.scss'
})
export class TongueToCaseComponent {
  @ViewChild(TongueSelectDialogComponent) tongueSelectDialog: TongueSelectDialogComponent;
  pageSize = 50;
  pageNumber = 0;
  sort = 1;
  selectedTongue: SimpleTongueDto = null;
  isMatch: number = 1;
  totalCount = 0;
  cases: SpecificTongueCaseDto[] = [];
  activeIndex = -1;
  tongues: {type: string, tongues: SimpleTongueDto[]}[] = [];
  tongueData: JsonTongue = null;
  jumpPage: number = null;
  tagOptions: SelectItem[] = [
    {label: 'No', value: 0},
    {label: 'Yes', value: 1}
  ];

  constructor(
    private appService: AppService,
    private msgService: MessageService
  ) {}

  ngOnInit(): void {
    const data = {
      type: 0,
      includeIcon: 1,
      includeCount: 1
    };
    this.appService.get('data', 'getSimpleTongues', data).subscribe((res: SimpleTongueDto[]) => {
      this.tongues.push({type: 'Shape', tongues: res.filter(t => t.type === 1)});
      this.tongues.push({type: 'Color', tongues: res.filter(t => t.type === 2)});
      this.tongues.push({type: 'Coating', tongues: res.filter(t => t.type === 3)});
    });
  }

  get totalPages(): number {
    return Math.ceil(this.totalCount / this.pageSize);
  }

  get pages(): number[] {
    const total = this.totalPages;
    if (total <= 0) return [];

    // Show 3 pages before and 3 pages after current pageNumber
    const start = Math.max(0, this.pageNumber - 3);
    const end = Math.min(total, this.pageNumber + 4);

    const pages = [];
    for (let i = start; i < end; i++) {
      pages.push(i);
    }
    return pages;
  }

  async onJumpPageEnter() {
    if (this.jumpPage === null || this.jumpPage === undefined) return;
    const targetPage = this.jumpPage - 1;
    if (targetPage >= 0 && targetPage < this.totalPages) {
      this.pageNumber = targetPage;
      this.jumpPage = null;
      await this.fetchCases();
    } else {
      alert('Page ' + this.jumpPage + ' does not exist. Total pages: ' + this.totalPages);
    }
  }

  async search() {

    if (!this.selectedTongue) {
      this.msgService.add(new ToastMsg(SeverityEnum.error, 'No tongue selected', ''));
      return;
    }
    this.pageNumber = 0;
    await this.fetchCases();
  }

  async fetchCases() {
    this.appService.setBlock(true);
    this.activeIndex = -1;
    const data = {
      tongueId: this.selectedTongue.id,
      isMatch: this.isMatch,
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      sort: this.sort
    };
    try {
      this.totalCount = (await firstValueFrom(this.appService.get('data', 'countCasesByTongue', data))).count;
      this.cases = await firstValueFrom(this.appService.get('data', 'getCasesByTongue', data));
      window.scrollTo(0, 0);
    } catch (e) {
      console.error(e);
    } finally {
      this.appService.setBlock(false);
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (this.cases.length === 0) return;
    
    // Ignore hotkeys when user is focused on an input or textarea
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (this.activeIndex < this.cases.length - 1) {
        this.activeIndex++;
        this.scrollToActiveCase();
      } else if (this.activeIndex === -1) {
        this.activeIndex = 0;
        this.scrollToActiveCase();
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (this.activeIndex > 0) {
        this.activeIndex--;
        this.scrollToActiveCase();
      } else if (this.activeIndex === -1) {
        this.activeIndex = 0;
        this.scrollToActiveCase();
      }
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      if (this.activeIndex >= 0 && this.activeIndex < this.cases.length) {
        this.changeIsTagged(this.cases[this.activeIndex], 0);
      }
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      if (this.activeIndex >= 0 && this.activeIndex < this.cases.length) {
        this.changeIsTagged(this.cases[this.activeIndex], 1);
      }
    }
  }

  private scrollToActiveCase() {
    setTimeout(() => {
      const element = document.getElementById('case-' + this.activeIndex);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  async onPageClick(page: number) {
    if (page === this.pageNumber || page < 0 || page >= this.totalPages) {
      return;
    }
    this.pageNumber = page;
    await this.fetchCases();
  }

  startSelectTongue() {
    this.tongueSelectDialog.setShow(true);
  }

  selectTongue(tongue: SimpleTongueDto) {
    this.selectedTongue = tongue;
    this.tongueSelectDialog.setShow(false);
    const data = {
      tongueId: this.selectedTongue.id,
      includeImage: 0
    };
    this.appService.get('data', 'getTongue', data).subscribe((res: JsonTongue) => {
      this.tongueData = res;
    });
  }

  changeIsTagged(patientCase: SpecificTongueCaseDto, newValue: number) {
    const oldVal = patientCase.isTagged ?? 0;
    if (oldVal === newValue) {
      return;
    }

    patientCase.isTagged = newValue;
    const data = {
      caseId: patientCase.caseId,
      tongueId: this.selectedTongue.id,
      location: 0,
      isAdd: newValue
    };
    this.appService.post('data', 'updateSpecificTongueCase', data).subscribe({
      next: (res: SpecificTongueCaseDto) => {
        this.msgService.add(new ToastMsg(SeverityEnum.success, 'Succeed to update the case id = ' + patientCase.caseId, ''));
      },
      error: (err) => {
        console.error('Update failed', err);
        // Revert UI state on error?
        patientCase.isTagged = oldVal === 0 ? null : oldVal; 
      }
    });
  }

  getTaggerStr(patientCase: SpecificTongueCaseDto): string {
    if (patientCase.locations.length === 1) {
      return patientCase.locations[0].tagger.name;
    } else {
      return patientCase.locations.map(l => l.tagger.name).join(', ');
    }
  }
}
